import React, { useCallback, useEffect, useRef, useState } from 'react'

import styled from 'styled-components';
import NEDNodes from '../../editor/nodes/NEDNodes';
import { ParagraphNode } from 'lexical';
import { CustomParagraphNode } from '../../editor/nodes/CustomParagraphNode';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import NEDTheme from '../../editor/themes/NEDTheme';
import { hydrateImagePaths } from '../../editor/utils/serializeImages';
import { IndentClassnames } from '../../editor/EditorContext';
import ImageModal from './ImageModal';

export default function PieceContent(
  {content}: {content: string}
) {
  const json = JSON.parse(content);
  let editorState;
  let editorMode = 'scale';
  let simplePageWidth;

  if ('editorMode' in json) {
    editorMode = json.editorMode || 'scale';
    editorState = JSON.stringify(json.editorState);
    simplePageWidth = json.simplePageWidth;
  } else {
    editorState = content;
  }
  const initialConfig = {
    namespace: 'NED',
    nodes: [...NEDNodes, {
      replace: ParagraphNode,
      with: (node: ParagraphNode) => new CustomParagraphNode()
    }],
    theme: NEDTheme,
    editorState: hydrateImagePaths(editorState),
    onError: (error: Error) => {
      // console.error('Content Error: ', error);
      // throw error;
    },
    editable: false,
  };

  const wrapperRef = useRef<HTMLElement | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState('');

  const adjustScale = useCallback((node?: HTMLElement) => {
    const wrapper = node || wrapperRef.current;
    if (wrapper) {
      const parent = wrapper.parentElement;
      if (parent) {
        const viewportWidth = parent.offsetWidth;
        const scaleFactor = viewportWidth / 1000;
        wrapper.style.transform = `scale(${scaleFactor})`;
        parent.style.height = `${wrapper.offsetHeight * scaleFactor}px`;
      }
    }
  }, []);

  const paragraphsToSimpleMode = useCallback(() => {
    const paragraphs = document.querySelectorAll(`.${IndentClassnames.portrait}`) as NodeListOf<HTMLElement>;
    paragraphs.forEach((paragraph) => {
      paragraph.classList.remove(IndentClassnames.portrait);
      paragraph.classList.add(IndentClassnames.simple);
    });
  }, []);

  // if simple mode on mobile make fonts smaller by 0.125%
  const simpleModeMobileFontSize = useCallback(() => {
    const spans = document.querySelectorAll(`.${IndentClassnames.simple} *`) as NodeListOf<HTMLElement>;
    spans.forEach((span) => {
      const fontSize = window.getComputedStyle(span).fontSize;
      if (fontSize) {
        const size = `calc(${fontSize} * 0.75)`;
        span.style.fontSize = size;
      }
    });
  }, [])

  const prepareSimpleMode = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (wrapper === null) return;
    wrapper.style.visibility = 'hidden';

    paragraphsToSimpleMode();
    if (window.innerWidth <= 768) {
      simpleModeMobileFontSize();
    }

    wrapper.style.visibility = 'visible';
  }, [paragraphsToSimpleMode, simpleModeMobileFontSize]);

  const addImageModal = useCallback((node: HTMLElement) => {
    const imageWrappers = node.querySelectorAll('.inline-image,.sticky-image');
    imageWrappers.forEach((w) => {
      w.addEventListener('click', () => {
        const image = w.querySelector('img');
        if (image) {
          setImageModalSrc(image.src);
          setIsImageModalOpen(true);
        }
      });
    });
  }, []);

  const removeBottomPaddingForInlineImage = useCallback(() => {
    // so that inline images would stick together
    const inlineImages = document.querySelectorAll('.inline-image') as NodeListOf<HTMLElement>;
    inlineImages.forEach((image) => {
      image.style.height = 'auto';
      const parent = image.parentElement;
      if (parent) {
        parent.style.lineHeight = '0px';
      }
    });
  }, []);

  // make sure the scale is adjusted after the text is rendered
  const leftAdjustScaleAttempts = useRef(20);
  const runAdjustScale = useCallback(() => {
    if (leftAdjustScaleAttempts.current <= 0 || !wrapperRef.current) return;
    setTimeout(() => {
      adjustScale();
      leftAdjustScaleAttempts.current--;
      runAdjustScale();
    }, 1000);
  }, [adjustScale]);

  const onRender = useCallback((node: HTMLElement | null) => {
    if (!node) return;
    wrapperRef.current = node;
    addImageModal(node);
    removeBottomPaddingForInlineImage();
    if (editorMode === 'scale') {
      runAdjustScale()
    }
  }, [addImageModal, removeBottomPaddingForInlineImage, editorMode, runAdjustScale]);

  useEffect(() => {
    if (editorMode === 'scale') {
      adjustScale();
      window.addEventListener('resize', () => adjustScale());
    }

    if (editorMode === 'simple') {
      prepareSimpleMode();
    }
      

    return () => {
      window.removeEventListener('resize', () => adjustScale());
    };
  }, [editorMode, adjustScale, prepareSimpleMode]);

  return (
    <>
    <Wrapper
      ref={onRender}
      $editorMode={editorMode}
      $simplePageWidth={simplePageWidth}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin 
          contentEditable={
          <ContentEditable
            className='editor-input'
          />
      }
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </LexicalComposer>
    </Wrapper>
    <ImageModal
        src={imageModalSrc}
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
      />
    </>
  )
}

const Wrapper = styled.div<{
  $editorMode: string;
  $simplePageWidth?: string;
}>(
  (props) => `
  position: relative;
  ${props.$editorMode === 'scale' ? `
    width: 1000px;
    transform-origin: top left;
  `: ''}
  ${props.$editorMode === 'simple' ? `
    margin: 0 auto;
    width: 92%;
    overflow-x: hidden;
  `: ''}

  -moz-text-size-adjust: none;
  -webkit-text-size-adjust: none;
  text-size-adjust: none;

  @media (min-width: 768px) {
    ${props.$editorMode === 'simple' ? `
    width: ${props.$simplePageWidth}vw;
  `: ''}
  }
`);